import { useCallback, useEffect, useState } from "react";
import { RotateCcw, RotateCw, Download } from "lucide-react";
import { Button } from "Components/Button";
import { createToast } from "Utils/toastFunc";
import { getMedicalClaimFileUnit } from "Requests/Partners/PreBudgets";
import { Box } from "styles/globalComponents";
import { DocumentItem } from "./DocumentItem";
import { useItemsContext } from "../../hooks/useItemsContext";
import {
  buildPreOrderDocumentDownloadName,
  triggerBrowserBlobDownload,
} from "./downloadDocumentFile";

interface DocumentsProps {
  files: { file_path: string }[] | undefined;
}

export const Documents = ({ files }: DocumentsProps) => {
  const [tabSelected, setTabSelected] = useState(1);
  const [rotations, setRotations] = useState<{ [key: string]: number }>({});
  const [downloadingFilePath, setDownloadingFilePath] = useState<string | null>(
    null
  );
  const { fileBlobs, isLoadingFiles } = useItemsContext();

  useEffect(() => {
    setTabSelected(1);
    setRotations({});
  }, [files]);

  const currentFileView = files?.[tabSelected - 1];
  const currentFileBlob = currentFileView
    ? fileBlobs[currentFileView.file_path]
    : null;

  const currentRotation = currentFileView
    ? rotations[currentFileView.file_path] || 0
    : 0;

  const handleRotateLeft = () => {
    if (currentFileView) {
      setRotations((prev) => ({
        ...prev,
        [currentFileView.file_path]:
          (prev[currentFileView.file_path] || 0) - 90,
      }));
    }
  };

  const handleRotateRight = () => {
    if (currentFileView) {
      setRotations((prev) => ({
        ...prev,
        [currentFileView.file_path]:
          (prev[currentFileView.file_path] || 0) + 90,
      }));
    }
  };

  const handleOpenOriginalLink = () => {
    if (currentFileView?.file_path) {
      window.open(currentFileView.file_path, "_blank");
    }
  };

  const handleDownloadFile = useCallback(
    async (currentBlob: Blob | null | undefined) => {
      const filePath = currentFileView?.file_path;
      if (!filePath) {
        createToast("error", "Arquivo não disponível para download.");
        return;
      }

      const fileIndex = Math.max(0, tabSelected - 1);
      setDownloadingFilePath(filePath);

      try {
        const blob = currentBlob ?? (await getMedicalClaimFileUnit(filePath));
        const filename = buildPreOrderDocumentDownloadName(
          filePath,
          fileIndex,
          blob.type
        );
        triggerBrowserBlobDownload(blob, filename);
      } catch {
        createToast("error", "Não foi possível baixar o arquivo.");
      } finally {
        setDownloadingFilePath(null);
      }
    },
    [currentFileView?.file_path, tabSelected]
  );

  return (
    <div className="documents-preview-style">
      <div className="tabs">
        <div
          className="tabs-left"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {files?.map((file, index) => (
            <div
              key={file.file_path}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Button
                type="button"
                onClick={() => setTabSelected(index + 1)}
                outlined
                {...(index + 1 === tabSelected && { outlined: false })}
              >
                Arquivo {index + 1}
              </Button>
            </div>
          ))}
        </div>
        <div className="tabs-right">
          <Button onClick={handleRotateLeft} outlined>
            <RotateCcw size={16} />
          </Button>
          <Button onClick={handleRotateRight} outlined>
            <RotateCw size={16} />
          </Button>
          <Button
            type="button"
            outlined
            disabled={
              downloadingFilePath === currentFileView?.file_path ||
              (!currentFileBlob && isLoadingFiles)
            }
            onClick={async () => {
              await handleDownloadFile(currentFileBlob);
            }}
          >
            <Download size={16} />
            <span>Baixar</span>
          </Button>
        </div>
      </div>

      <div className="documents-preview">
        {currentFileView && (
          <Box
            display="flex"
            direction="column"
            justify="center"
            align="center"
            mt="30"
          >
            <DocumentItem
              fileBlob={currentFileBlob}
              isLoading={isLoadingFiles}
              rotation={currentRotation}
            />
          </Box>
        )}
      </div>
    </div>
  );
};
